import { useState, useEffect, useCallback } from 'react';
import FinanceConditionPayable from './FinanceConditionPayable';
import { ErrorState } from 'types/Payable';
import {
  DepositPayable,
  FormCondition,
  FormConditionCondition,
  FormConditionDayType,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';
import { isEqual } from 'lodash-es';
import { AgxRadioGroup, AgxSpacer } from '@urbanx/agx.ui-components2';
import { RadioGroupOptions } from '@urbanx/agx.ui-components2';

interface FinanceConditionProps {
  id: string;
  label: string;
  onValueChanged: (change: { id: string; value: FormCondition }) => void;
  defaultValue: FormCondition;
  validate?: boolean;
  error?: ErrorState;
  specialConditions: SpecialConditionData[];
  campaignId: string;
}

const subjectToFinanceValue = 'subjectToFinance';

const radioOptions: RadioGroupOptions[] = [
  {
    label: 'Not subject to finance',
    value: FormConditionCondition.NotSubjectToFinance,
  },
  {
    label: 'Subject to finance',
    value: subjectToFinanceValue,
  },
];

const FinanceCondition = ({
  id,
  label,
  onValueChanged,
  defaultValue,
  validate,
  error,
  specialConditions,
  campaignId,
}: FinanceConditionProps) => {
  const [financeCondition, setFinanceCondition] = useState<FormCondition>(
    () => ({
      ...defaultValue,
      condition:
        defaultValue?.condition ?? FormConditionCondition.NotSubjectToFinance,
    })
  );

  useEffect(() => {
    if (isEqual(defaultValue, financeCondition)) return;

    onValueChanged({ id, value: financeCondition });
  }, [financeCondition]);

  const onFinanceConditionChanged = useCallback(
    (change: { id: string; value: FormCondition }) => {
      setFinanceCondition(prev => ({
        ...prev,
        ...change.value,
      }));
    },
    []
  );

  const onRadioGroupChange = useCallback((val: string) => {
    if (val === FormConditionCondition.NotSubjectToFinance) {
      setFinanceCondition(prev => ({
        ...prev,
        condition: FormConditionCondition.NotSubjectToFinance,
        depositPayable: DepositPayable.NDaysFrom,
        dayType: FormConditionDayType.Days,
        other: undefined,
        daysFromDate: undefined,
        referenceDate: undefined,
        specialConditionId: undefined,
      }));
    } else {
      setFinanceCondition(prev => ({
        ...prev,
        condition: FormConditionCondition.NdaysFromDate,
      }));
    }
  }, []);

  return (
    <AgxRadioGroup
      id={`financeConditionRadioGroup-${id}`}
      layout={'vertical'}
      label={label}
      options={radioOptions}
      value={
        financeCondition.condition ===
        FormConditionCondition.NotSubjectToFinance
          ? FormConditionCondition.NotSubjectToFinance
          : subjectToFinanceValue
      }
      onChange={onRadioGroupChange}
      subContent={
        financeCondition.condition !==
        FormConditionCondition.NotSubjectToFinance ? (
          <AgxSpacer direction="vertical" padding="md" fill>
            <FinanceConditionPayable
              id={id}
              key={id}
              onValueChanged={onFinanceConditionChanged}
              defaultValue={financeCondition}
              validate={validate}
              error={error}
              required={true}
              specialConditions={specialConditions}
              campaignId={campaignId}
            />
          </AgxSpacer>
        ) : undefined
      }
    />
  );
};

export default FinanceCondition;
